import { useCallback, useEffect, useState } from "react";
import {
  ClientSafeHandoffViewV1Schema,
  type ClientSafeHandoffViewV1,
} from "@enrein/shared-contracts";
import { toClientApiErrorMessage, toHandoffIssuanceLabel } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

export function HandoffPanel({
  api,
  clientLinkId,
  token,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
}) {
  const [handoff, setHandoff] = useState<ClientSafeHandoffViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const view = ClientSafeHandoffViewV1Schema.parse(await api.getHandoff(clientLinkId, token));
      setHandoff(view);
      setError(null);
    } catch (err) {
      setHandoff(null);
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }, [api, clientLinkId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download() {
    try {
      const blob = await api.downloadHandoff(clientLinkId, token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "client-safe-handoff.zip";
      link.rel = "noreferrer";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)));
    }
  }

  if (error) {
    return (
      <section className="value-card value-card--featured handoff-card" aria-label="Ownership handoff">
        <p className="value-card-eyebrow">Departure Kit</p>
        <h2 className="value-card-headline">Ownership handoff</h2>
        <p className="warning">{error}</p>
      </section>
    );
  }

  if (!handoff) {
    return (
      <section className="value-card value-card--featured handoff-card loading-panel" aria-label="Ownership handoff">
        <p className="value-card-eyebrow">Departure Kit</p>
        <h2 className="value-card-headline">Ownership handoff</h2>
        <p>Loading handoff details…</p>
      </section>
    );
  }

  return (
    <section className="value-card value-card--featured handoff-card" aria-label="Ownership handoff">
      <p className="value-card-eyebrow">Departure Kit</p>
      <h2 className="value-card-headline">{handoff.headline}</h2>
      <p>{handoff.subcopy}</p>
      {handoff.mismatchBanner ? <p className="warning">{handoff.mismatchBanner}</p> : null}
      <span className="value-card-status value-card-status--ready">
        {toHandoffIssuanceLabel(handoff.issuanceStatus)}
      </span>
      <ul>
        {handoff.checklist.map((item) => (
          <li key={item.label}>
            <strong>{item.label}:</strong> {item.included ? "Included" : "Not yet included"}
          </li>
        ))}
      </ul>
      <details>
        <summary>What we do not promise</summary>
        <ul>
          {handoff.whatWeDoNotPromise.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
      <div className="action-row">
        <button
          type="button"
          className="btn-primary"
          disabled={!handoff.downloadAvailable}
          onClick={() => void download()}
        >
          Download ownership bundle
        </button>
        <button type="button" className="btn-secondary" onClick={() => void load()}>
          Refresh
        </button>
      </div>
    </section>
  );
}
